import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  CornerDownLeft,
  LayoutGrid,
  Loader2,
  LockOpen,
  X,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import {
  apiClient,
  apiMutate,
  apiStreamClient,
  getApiErrorMessage,
  isApiError,
} from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";
import { readStreamEvents } from "~/utils/streamReader";
import { McpAppFrame } from "./McpAppFrame";

// ── Types ─────────────────────────────────────────────────────────────────────

interface BreakdownRow {
  label: string | null;
  count: number;
}

interface Breakdown {
  name: string;
  data: BreakdownRow[];
}

// Partial — only the fields used by this component are listed.
interface Criterion {
  id: string;
  participant_count?: number | null;
  participant_breakdowns?: Breakdown[] | null;
}

export interface CohortCardData {
  id: number;
  name: string;
  dataset_id?: string | null;
  llm_description?: string | null;
  user_description?: string | null;
  // Number of participants matching the cohort's filters.
  participant_count?: number | null;
  // Total participants in the dataset (the denominator for "X of Y total").
  // This is dataset-level, not cohort-level.
  starting_participant_count?: number | null;
  pending_count?: boolean;
  // Demographic breakdown of the full dataset population (not the filtered cohort).
  // Used as reference context alongside the cohort count.
  starting_participant_breakdowns?: Breakdown[] | null;
  criteria?: Criterion[] | null;
  subject_label?: string | null;
  updated_at?: string | null;
}

interface CohortCardProps {
  cohort: CohortCardData;
}

type ExportState = "idle" | "exporting" | "done" | "error";

interface ProgressItem {
  message: string;
  status: "loading" | "done";
}

interface ProgressStreamEvent {
  kind: "progress";
  message: string;
}

interface ResultStreamEvent {
  kind: "result";
  id: string;
  name: string;
}

interface ErrorStreamEvent {
  kind: "error";
  message: string;
}

type StreamEvent = ProgressStreamEvent | ResultStreamEvent | ErrorStreamEvent;

interface ExportStreamingPanelProps {
  progressItems: ProgressItem[];
  exportState: ExportState;
  exportError: string | null;
  resultDatasetId: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString();
}

// ── useExportDataset ──────────────────────────────────────────────────────────

function useExportDataset(cohortId: number, cohortName: string, userEmail: string | undefined) {
  // AbortController must survive re-renders without triggering them — legitimate ref use case.
  const abortRef = useRef<AbortController | null>(null);
  // DOM focus on the name input — legitimate ref use case.
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks whether the component has mounted so focus only fires on re-export, not on mount.
  const hasMountedRef = useRef(false);
  const [exportState, setExportState] = useState<ExportState>("idle");
  const [datasetName, setDatasetName] = useState(cohortName);
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [resultDatasetId, setResultDatasetId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (exportState === "idle") {
      inputRef.current?.focus();
    }
  }, [exportState]);

  const showStreamingPanel =
    exportState === "exporting" || exportState === "done" || exportState === "error";

  async function handleExportConfirm(nameOverride?: string) {
    const name = nameOverride ?? datasetName;
    if (!userEmail || !name.trim()) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setExportState("exporting");
    setProgressItems([]);
    setResultDatasetId(null);
    setExportError(null);

    try {
      const response = await apiStreamClient(
        `/api/cohort-explorer/v1/cohorts/${cohortId}/child-dataset`,
        {
          method: "POST",
          body: JSON.stringify({ name: name.trim() }),
          signal: controller.signal,
        }
      );

      let succeeded = false;

      for await (const event of readStreamEvents(response, controller.signal)) {
        const data = event as StreamEvent;
        if (data.kind === "progress") {
          setProgressItems((prev) => {
            const updated = prev.map((item, i) =>
              i === prev.length - 1 ? { ...item, status: "done" as const } : item
            );
            return [...updated, { message: data.message, status: "loading" }];
          });
        } else if (data.kind === "result") {
          setProgressItems((prev) => [
            ...prev.map((item) => ({ ...item, status: "done" as const })),
            { message: `Dataset "${data.name}" created successfully.`, status: "done" },
          ]);
          setResultDatasetId(data.id);
          succeeded = true;
        } else if (data.kind === "error") {
          throw new Error(data.message);
        } else {
          console.warn("Unrecognized stream event kind:", (data as { kind: string }).kind);
        }
      }

      if (succeeded) {
        setExportState("done");
      } else {
        throw new Error("No result received from server.");
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setExportState("error");
      setExportError(
        isApiError(err)
          ? getApiErrorMessage(err)
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred."
      );
    }
  }

  function handleExportClick() {
    if (exportState === "done") {
      setProgressItems([]);
      setResultDatasetId(null);
      setExportError(null);
      setDatasetName(cohortName);
      void handleExportConfirm(cohortName);
      return;
    }
    if (exportState === "error") {
      setProgressItems([]);
      setResultDatasetId(null);
      setExportError(null);
    }
    void handleExportConfirm();
  }

  return {
    exportState,
    datasetName,
    setDatasetName,
    progressItems,
    resultDatasetId,
    exportError,
    showStreamingPanel,
    inputRef,
    handleExportConfirm,
    handleExportClick,
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BreakdownGroup({ group, total }: { group: Breakdown; total: number }) {
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {group.name}
      </p>
      <div className="space-y-1.5">
        {group.data.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-xs text-muted-foreground truncate">
              {row.label ?? "—"}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-muted-foreground/40"
                style={{ width: `${(row.count / Math.max(total, 1)) * 100}%` }}
              />
            </div>
            <span className="w-12 shrink-0 text-right text-xs tabular-nums text-foreground">
              {fmt(row.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportStreamingPanel({
  progressItems,
  exportState,
  exportError,
  resultDatasetId,
}: ExportStreamingPanelProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <CornerDownLeft className="size-3.5" aria-hidden />
        CE response streamed into conversation
      </p>
      <ul className="space-y-2">
        {progressItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            {item.status === "loading" ? (
              <Loader2
                className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
                aria-hidden
              />
            ) : i === progressItems.length - 1 && exportState === "done" ? (
              <Check
                className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400"
                aria-hidden
              />
            ) : (
              <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span
              className={cn(
                i === progressItems.length - 1 && exportState === "done"
                  ? "font-medium text-green-700 dark:text-green-400"
                  : "text-muted-foreground"
              )}
            >
              {item.message}
            </span>
          </li>
        ))}
        {exportState === "error" && exportError && (
          <li className="flex items-start gap-2 text-sm">
            <X className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <span className="text-destructive">{exportError}</span>
          </li>
        )}
      </ul>
      {exportState === "done" && resultDatasetId && (
        <a
          href={`/nextgen/datasets/${resultDatasetId}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground",
            "hover:bg-muted transition-colors"
          )}
        >
          Open in catalog →
        </a>
      )}
    </div>
  );
}

const { VITE_CATALOG_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;
const CE_MCP_APP_ENABLED = import.meta.env.VITE_CE_MCP_APP_ENABLED === "true";
const CE_MCP_SERVER_ID = "cohort_explorer";
const CE_MCP_RESOURCE_URI = "ui://cohort-review/app.html";

interface AccessRequestRecord {
  id: string;
  cohort_id: number;
  status: "pending" | "processing" | "approved" | "denied" | "failed" | "cancelled";
  updated_at: string;
}

interface PaginatedAccessRequests {
  items: AccessRequestRecord[];
  total: number;
}

function AccessRequestButton({
  cohortId,
  datasetId,
  cohortUpdatedAt,
}: {
  cohortId: number;
  datasetId?: string | null;
  cohortUpdatedAt?: string | null;
}) {
  const user = useUser();
  const {
    data: existingRequests,
    error: fetchError,
    isLoading: isRequestsLoading,
    mutate: revalidateRequests,
  } = useFetcher<PaginatedAccessRequests>(
    user?.email && datasetId
      ? `/api/cohort-explorer/v1/access-requests?parent_dataset_id=${datasetId}&cohort_id=${cohortId}&user_id=${user.email}`
      : null
  );
  const rawLatestRequest = existingRequests?.items?.[0];
  const isStaleTerminalRequest =
    rawLatestRequest &&
    (rawLatestRequest.status === "cancelled" || rawLatestRequest.status === "denied") &&
    cohortUpdatedAt &&
    new Date(cohortUpdatedAt) > new Date(rawLatestRequest.updated_at);
  const latestRequest = isStaleTerminalRequest ? undefined : rawLatestRequest;

  const [state, setState] = useState<"idle" | "submitting" | "submitted" | "cancelling" | "error">(
    "idle"
  );
  const [intendedUse, setIntendedUse] = useState("");
  const [showForm, setShowForm] = useState(false);

  const handleCancel = async () => {
    if (!latestRequest) return;
    setState("cancelling");
    try {
      await apiMutate(
        `/api/cohort-explorer/v1/access-requests/${latestRequest.id}/cancel`,
        "POST",
        undefined,
        {
          expects204: true,
        }
      );
      void revalidateRequests();
      setState("idle");
    } catch {
      setState("error");
    }
  };

  const handleSubmit = async () => {
    if (!intendedUse.trim()) return;
    setState("submitting");
    try {
      await apiMutate("/api/cohort-explorer/v1/access-requests", "POST", {
        cohort_id: cohortId,
        intended_use: intendedUse.trim(),
      });
      trackEvent("cohort_access_request_submitted", {
        cohort_id: String(cohortId),
        dataset_id: datasetId ?? undefined,
      });
      setState("submitted");
      void revalidateRequests();
    } catch {
      setState("error");
    }
  };

  if (isRequestsLoading) return null;

  if (fetchError) {
    return (
      <div
        className={cn(
          "rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950",
          "px-4 py-2.5 text-center text-sm font-medium text-red-800 dark:text-red-200"
        )}
      >
        Unable to load access request status
      </div>
    );
  }

  if (state === "submitted" || latestRequest) {
    const status = latestRequest?.status ?? "pending";
    const config = {
      pending: {
        border: "border-yellow-300 dark:border-yellow-700",
        bg: "bg-yellow-50 dark:bg-yellow-950",
        text: "text-yellow-800 dark:text-yellow-200",
        message: "Access request pending review",
      },
      processing: {
        border: "border-yellow-300 dark:border-yellow-700",
        bg: "bg-yellow-50 dark:bg-yellow-950",
        text: "text-yellow-800 dark:text-yellow-200",
        message: "Access request is being processed",
      },
      approved: {
        border: "border-green-300 dark:border-green-700",
        bg: "bg-green-50 dark:bg-green-950",
        text: "text-green-800 dark:text-green-200",
        message: "Access request approved",
      },
      denied: {
        border: "border-red-300 dark:border-red-700",
        bg: "bg-red-50 dark:bg-red-950",
        text: "text-red-800 dark:text-red-200",
        message: "Access request denied",
      },
      failed: {
        border: "border-red-300 dark:border-red-700",
        bg: "bg-red-50 dark:bg-red-950",
        text: "text-red-800 dark:text-red-200",
        message: "Access request failed — the dataset owner needs to retry approval",
      },
      cancelled: {
        border: "border-border",
        bg: "bg-muted",
        text: "text-muted-foreground",
        message:
          "Access request was cancelled by you. Please submit a new request if you still need access.",
      },
    }[status];
    return (
      <div
        className={cn(
          "rounded-lg border px-4 py-2.5 text-center text-sm font-medium",
          config.border,
          config.bg,
          config.text
        )}
      >
        <p>{config.message}</p>
        {status === "pending" && (
          <button
            type="button"
            onClick={handleCancel}
            disabled={state === "cancelling"}
            className="mt-2 text-xs underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state === "cancelling" ? "Cancelling…" : "Cancel request"}
          </button>
        )}
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => setShowForm(true)}>
          <LockOpen className="size-4" aria-hidden />
          Submit access request
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        className={cn(
          "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
        rows={3}
        placeholder="Describe how you intend to use this data..."
        value={intendedUse}
        onChange={(e) => setIntendedUse(e.target.value)}
        disabled={state === "submitting"}
      />
      {state === "error" && (
        <p className="text-xs text-destructive">Failed to submit request. Please try again.</p>
      )}
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => {
            setShowForm(false);
            setIntendedUse("");
            setState("idle");
          }}
          disabled={state === "submitting"}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={handleSubmit}
          disabled={!intendedUse.trim() || state === "submitting"}
        >
          {state === "submitting" ? "Submitting…" : "Submit"}
        </Button>
      </div>
    </div>
  );
}

interface DatasetAccessResponse {
  access_level: "owner" | "analyze" | "preview" | null;
}

function useIsPreviewUser(datasetId: string | null | undefined): {
  isPreview: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useFetcher<DatasetAccessResponse>(
    datasetId ? `${VITE_CATALOG_API}/datasets/${datasetId}` : null
  );
  if (isLoading || data === undefined) return { isPreview: false, isLoading: true };
  const level = data?.access_level;
  return { isPreview: level === null || level === "preview", isLoading: false };
}

// ── useViewFilters ────────────────────────────────────────────────────────────

function useViewFilters(cohort: CohortCardData) {
  const [html, setHtml] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  async function handleClick() {
    if (isVisible) {
      setIsVisible(false);
      return;
    }
    if (html !== null) {
      setIsVisible(true);
      return;
    }
    setIsLoading(true);
    setHasError(false);
    const params = new URLSearchParams({ server_id: CE_MCP_SERVER_ID, uri: CE_MCP_RESOURCE_URI });
    try {
      const result = await apiClient<string>(
        `${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`,
        { expectsText: true }
      );
      setHtml(result);
      setIsVisible(true);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }

  const toolResult = useMemo(() => JSON.stringify(cohort), [cohort]);

  return { html, isVisible, isLoading, hasError, handleClick, toolResult };
}

// ── Main card ─────────────────────────────────────────────────────────────────

export function CohortCard({ cohort }: CohortCardProps) {
  const user = useUser();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const { isPreview: isPreviewUser, isLoading: isAccessLoading } = useIsPreviewUser(
    cohort.dataset_id
  );
  const {
    html,
    isVisible,
    isLoading: isFiltersLoading,
    hasError: filtersError,
    handleClick: handleViewFilters,
    toolResult,
  } = useViewFilters(cohort);
  const {
    exportState,
    datasetName,
    setDatasetName,
    progressItems,
    resultDatasetId,
    exportError,
    showStreamingPanel,
    inputRef,
    handleExportConfirm,
    handleExportClick,
  } = useExportDataset(cohort.id, cohort.name, user?.email);

  const desc = cohort.llm_description ?? cohort.user_description ?? null;
  const cohortCount = cohort.participant_count;
  const datasetTotal = cohort.starting_participant_count;
  const pending = cohort.pending_count ?? false;
  const pct =
    cohortCount != null && datasetTotal != null && datasetTotal > 0
      ? Math.round((cohortCount / datasetTotal) * 100)
      : null;
  const lastCriterion = cohort.criteria?.length
    ? cohort.criteria[cohort.criteria.length - 1]
    : null;
  const cohortParticipantBreakdowns =
    lastCriterion?.participant_breakdowns ?? cohort.starting_participant_breakdowns ?? [];
  const breakdownTotal = lastCriterion?.participant_count ?? cohortCount ?? 1;
  const hasBreakdowns = cohortParticipantBreakdowns.length > 0;

  return (
    <div className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden">
      <div className="p-4 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight text-foreground truncate">
              {cohort.name}
            </p>
            {desc && <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{desc}</p>}
          </div>
          <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            #{cohort.id}
          </span>
        </div>

        {/* Count + progress bar */}
        {pending ? (
          <p className="text-xs italic text-muted-foreground">Calculating…</p>
        ) : cohortCount != null ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-semibold tabular-nums text-primary">
                {fmt(cohortCount)}
              </span>
              {datasetTotal != null && (
                <span className="text-xs text-muted-foreground">
                  of {fmt(datasetTotal)} total
                  {pct != null && <span> · {pct}%</span>}
                </span>
              )}
            </div>
            {pct != null && (
              <div className="h-1 w-full rounded-full bg-primary/15">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
        ) : null}

        {/* Breakdown toggle */}
        {hasBreakdowns && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBreakdown((v) => !v)}
              className="-ml-2 h-7 gap-1 text-xs font-medium"
            >
              {showBreakdown ? (
                <ChevronUp className="size-3.5" aria-hidden />
              ) : (
                <ChevronDown className="size-3.5" aria-hidden />
              )}
              {showBreakdown ? "Hide" : "Show"} breakdowns
            </Button>
            {showBreakdown && (
              <div className="mt-2 rounded-lg bg-muted/50 px-3 py-2 space-y-3">
                {cohortParticipantBreakdowns.map((group, i) => (
                  <BreakdownGroup key={i} group={group} total={breakdownTotal} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* View filters toggle — sits directly above the MCP app frame */}
        <div className="flex flex-col gap-1.5 border-t border-border pt-3">
          <Button
            variant="outline"
            className="w-full gap-1.5"
            onClick={() => void handleViewFilters()}
            disabled={!CE_MCP_APP_ENABLED || isFiltersLoading}
          >
            {isFiltersLoading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <LayoutGrid className="size-4" aria-hidden />
            )}
            {isVisible ? "Hide filters" : "View filters"}
          </Button>
          {filtersError && (
            <p className="text-xs text-destructive text-center">Unable to load filter view</p>
          )}
        </div>

        {/* MCP app frame — full-width between the two button groups.
            -mx-4 must match the parent p-4; update both together. */}
        {isVisible && html && (
          <div className="-mx-4">
            <McpAppFrame html={html} title="Cohort Review" toolResult={toolResult} />
          </div>
        )}

        {/* Export action */}
        <div className="flex flex-col gap-2">
          {isAccessLoading ? null : isPreviewUser ? (
            <AccessRequestButton
              cohortId={cohort.id}
              datasetId={cohort.dataset_id}
              cohortUpdatedAt={cohort.updated_at}
            />
          ) : exportState !== "exporting" ? (
            <div className="flex items-center overflow-hidden rounded-lg border border-input">
              <Input
                ref={inputRef}
                className="h-9 flex-1 rounded-none border-0 shadow-none focus-visible:ring-0"
                type="text"
                value={datasetName}
                onChange={(e) => setDatasetName(e.target.value)}
                placeholder="Dataset name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && datasetName.trim()) void handleExportConfirm();
                }}
              />
              <Button
                className="rounded-l-none"
                onClick={handleExportClick}
                disabled={!datasetName.trim()}
              >
                Export as dataset →
              </Button>
            </div>
          ) : null}
          {!isPreviewUser && showStreamingPanel && (
            <ExportStreamingPanel
              progressItems={progressItems}
              exportState={exportState}
              exportError={exportError}
              resultDatasetId={resultDatasetId}
            />
          )}
        </div>
      </div>
    </div>
  );
}
